import { Stack } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledButton } from 'src/components/app-components/StyledButton';
import { ErrorTooltip } from 'src/components/app-components/StyledTooltip';
import { ANSWER_STATUS, AnswerStatus, FORM_TYPE } from 'src/constants';
import useAuth from 'src/hooks/useAuth';
import { usePermission } from 'src/hooks/usePermission';
import { useAppSelector } from 'src/redux/store';
import { useCreateSeriesAnswerData } from '../hooks/useCreateSeriesAnswerData';
import useEnqueteAnswerSave from '../hooks/useEnqueteAnswerSave';
import { SwitchReversionModalLayout } from './common/components/SwitchReversionModalLayout';
import { REVERSION, REVERSION_REQUEST } from './common/constants';
import { useReturnedHandleClick } from './common/hooks/useReturnedHandleClick';
export interface Props {
  respondentTargetId: string;
  confirmRequestId: string | null;
}

export const SeriesSurveySaveButtons: React.FC<Props> = (props): React.ReactElement => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isAnswered, commonSaveHandler } = useEnqueteAnswerSave(props.respondentTargetId);
  const { confirmRequestData, enqueteAnswerData, isEnqueteAnswerOpen, surveyDetailData } = useAppSelector((state) => state.enqueteAnswer);
  const { hasError, currency } = useAppSelector((state) => state.seriesAnswer);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isAdmin, isViewerAdmin, isSurveyOwner, isRespondentPerson, isAnswerEditor } = usePermission();
  const { handleReversionType, isReversionRequest } = useReturnedHandleClick(setIsOpen);
  const createSeriesAnswerData = useCreateSeriesAnswerData();
  const userId = null !== user ? user.email : '';
  const formType = surveyDetailData.surveyHeader?.formType;
  // NOTE: 編集可能（提出・一時保存）なユーザーの権限・ロール
  const canEdit = isAdmin || isSurveyOwner || isRespondentPerson || isAnswerEditor;
  // NOTE: 提出不可状態（入力エラー状態以外）：アンケートが実施中ではない OR 回答済み OR 編集可能なユーザーの権限・ロールを持っていない
  const canNotSubmit = !isEnqueteAnswerOpen || enqueteAnswerData.status === ANSWER_STATUS.ANSWERED || !canEdit;
  const marginTopValue = props.confirmRequestId && confirmRequestData.confirmerId === userId ? '20px' : '50px';

  const submitHandler = async (answerStatus: AnswerStatus) => {
    // 画面項目の入力処理にdebounceを使用しているため、保存または提出時に1秒待機し、入力内容が正しく更新されることを保証する
    await new Promise((resolve) => setTimeout(resolve, 1000));
    // 待機完了後、最新の状態を取得し、回答データを作成する
    const seriesAnswerData = createSeriesAnswerData();
    await commonSaveHandler(seriesAnswerData, answerStatus);
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2, md: 4 }} sx={{ marginTop: marginTopValue }}>
        {(isSurveyOwner || isAdmin || isViewerAdmin) && (
          <StyledButton
            onClick={() => handleReversionType(REVERSION)}
            isDisabled={!isEnqueteAnswerOpen || enqueteAnswerData.status !== ANSWER_STATUS.ANSWERED || !(isSurveyOwner || isAdmin)}>
            {t('button.reversion')}
          </StyledButton>
        )}
        {(isRespondentPerson || isAdmin || isViewerAdmin) && (
          <StyledButton
            onClick={() => handleReversionType(REVERSION_REQUEST)}
            isDisabled={!isEnqueteAnswerOpen || enqueteAnswerData.status !== ANSWER_STATUS.ANSWERED || !(isRespondentPerson || isAdmin)}>
            {t('button.requestReversion')}
          </StyledButton>
        )}
        <StyledButton
          onClick={() => submitHandler(ANSWER_STATUS.SAVED)}
          isDisabled={isAnswered || !canEdit || (formType === FORM_TYPE.SERIES && currency === '')}>
          {t('button.temporarilySave')}
        </StyledButton>
        <ErrorTooltip title={t('message.invalid') ?? ''} open={!canNotSubmit && hasError} placement="top" color="secondary" arrow>
          <span>
            <StyledButton onClick={() => submitHandler(ANSWER_STATUS.ANSWERED)} isDisabled={canNotSubmit || hasError}>
              {t(enqueteAnswerData.status === ANSWER_STATUS.RETURNED ? 'button.resubmission' : 'button.submit')}
            </StyledButton>
          </span>
        </ErrorTooltip>
      </Stack>
      <SwitchReversionModalLayout isOpen={isOpen} setIsOpen={setIsOpen} isRequest={isReversionRequest} />
    </>
  );
};
